import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  listLikersForTarget,
  type LikerRecordRow,
  type LikerSourceKind,
} from '../cache/db'
import { pullLikersFor, type LikersPullProgress } from '../api/pullLikers'

const LIKER_PILLS: { key: LikerSourceKind; label: string }[] = [
  { key: 'posts', label: 'posts' },
  { key: 'replies', label: 'replies' },
  { key: 'reposts', label: 'reposts' },
]

interface AggregatedLiker {
  did: string
  handle: string
  displayName?: string
  avatar?: string
  perSource: Record<LikerSourceKind, number>
  total: number
  latestLikedAt: string
}

function sortFn(mode: 'total' | 'recent') {
  return (a: AggregatedLiker, b: AggregatedLiker) =>
    mode === 'total'
      ? b.total - a.total || (a.latestLikedAt < b.latestLikedAt ? 1 : -1)
      : a.latestLikedAt < b.latestLikedAt
        ? 1
        : -1
}

export function LikersView({ targetHandle }: { targetHandle: string }) {
  const handleLc = targetHandle.toLowerCase()
  const navigate = useNavigate()
  const [enabled, setEnabled] = useState<Set<LikerSourceKind>>(
    () => new Set(['posts'] as LikerSourceKind[]),
  )
  const [rows, setRows] = useState<LikerRecordRow[]>([])
  const [busy, setBusy] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pull, setPull] = useState<{
    running: boolean
    progress: Record<string, LikersPullProgress>
    cancel: boolean
  } | null>(null)
  const [sortMode, setSortMode] = useState<'total' | 'recent'>('total')
  const [filter, setFilter] = useState('')

  async function refresh() {
    try {
      const all = await listLikersForTarget(handleLc)
      setRows(all)
      setLoadError(null)
    } catch (e) {
      // A rejected read used to be swallowed and rendered as "nothing
      // cached" — show the real reason (usually a broken/quota'd DB).
      setLoadError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    setBusy(true)
    void refresh().finally(() => setBusy(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLc])

  async function runPull() {
    setPull({ running: true, progress: {}, cancel: false })
    const ctrl = new AbortController()
    const cancelPoll = setInterval(() => {
      setPull((prev) => {
        if (prev?.cancel) ctrl.abort()
        return prev
      })
    }, 200)
    try {
      await pullLikersFor({
        targetHandle: handleLc,
        sources: Array.from(enabled),
        signal: ctrl.signal,
        onProgress: (p) => {
          setPull((prev) =>
            prev ? { ...prev, progress: { ...prev.progress, [p.source]: p } } : prev,
          )
        },
      })
    } finally {
      clearInterval(cancelPoll)
      await refresh()
      setPull((prev) => (prev ? { ...prev, running: false } : prev))
    }
  }

  function toggle(s: LikerSourceKind) {
    setEnabled((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      if (next.size === 0) next.add(s)
      return next
    })
  }

  const aggregated = useMemo<AggregatedLiker[]>(() => {
    const map = new Map<string, AggregatedLiker>()
    for (const r of rows) {
      if (!enabled.has(r.sourceKind)) continue
      const existing = map.get(r.likerDid)
      if (existing) {
        existing.perSource[r.sourceKind] = (existing.perSource[r.sourceKind] ?? 0) + 1
        existing.total += 1
        if (r.likedAt > existing.latestLikedAt) existing.latestLikedAt = r.likedAt
      } else {
        map.set(r.likerDid, {
          did: r.likerDid,
          handle: r.likerHandle,
          displayName: r.likerDisplayName,
          avatar: r.likerAvatar,
          perSource: { posts: 0, reposts: 0, replies: 0, [r.sourceKind]: 1 } as Record<
            LikerSourceKind,
            number
          >,
          total: 1,
          latestLikedAt: r.likedAt,
        })
      }
    }
    const out = Array.from(map.values())
    if (filter) {
      const f = filter.toLowerCase()
      return out
        .filter((a) => a.handle.toLowerCase().includes(f) || a.displayName?.toLowerCase().includes(f))
        .sort(sortFn(sortMode))
    }
    return out.sort(sortFn(sortMode))
  }, [rows, enabled, filter, sortMode])

  const totalEvents = useMemo(
    () => rows.filter((r) => enabled.has(r.sourceKind)).length,
    [rows, enabled],
  )

  return (
    <div className="flex flex-col gap-2 px-3 py-2 text-xs">
      {/* pull controls */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-widest text-ink-400">src</span>
        {LIKER_PILLS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => toggle(p.key)}
            className={clsx(
              'glass-button text-[10px]',
              enabled.has(p.key) && 'border-[color:var(--accent-strong)] text-white',
            )}
          >
            {p.label}
            <span className="ml-1 text-ink-400">{rows.filter((r) => r.sourceKind === p.key).length}</span>
          </button>
        ))}
        <button
          type="button"
          className="glass-button ml-auto text-[10px] font-bold"
          disabled={pull?.running}
          onClick={() => void runPull()}
          style={{ borderColor: '#fbbf24', color: '#fcd34d' }}
          title="walk app.bsky.feed.getLikes for every cached post in the enabled sources"
        >
          {pull?.running ? '🔍 pulling…' : '🔍 pull likers'}
        </button>
        {pull?.running && (
          <button
            type="button"
            className="glass-button text-[10px]"
            onClick={() => setPull((prev) => (prev ? { ...prev, cancel: true } : prev))}
          >
            ✕
          </button>
        )}
      </div>

      {/* stats */}
      <div className="text-[10px] text-ink-400">
        <strong className="text-white">{aggregated.length.toLocaleString()}</strong> likers ·{' '}
        {totalEvents.toLocaleString()} events
      </div>

      {/* pull progress */}
      {pull && (
        <div className="glass flex flex-wrap gap-1.5 px-2 py-1.5 text-[10px]">
          <strong className="text-white">{pull.running ? '🔍 pulling' : '✓ done'}</strong>
          {Object.values(pull.progress).map((p) => (
            <span key={p.source} className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5">
              {p.source}: {p.postsDone}/{p.postsTotal} · +{p.likersAdded}
            </span>
          ))}
          {Object.values(pull.progress)
            .filter((p) => p.error)
            .map((p) => (
              <span key={`${p.source}-err`} className="w-full break-all text-rose-300">
                ✕ {p.source}: {p.error}
              </span>
            ))}
        </div>
      )}

      {loadError && (
        <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-2 py-1.5 text-[10px] text-rose-200">
          cache unreadable: {loadError}
        </div>
      )}

      {busy && rows.length === 0 && (
        <div className="text-[10px] text-ink-300">loading…</div>
      )}

      {aggregated.length === 0 && !busy && !loadError && (
        <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-[10px] text-ink-300">
          nothing cached — hit 🔍 pull likers
        </div>
      )}

      {/* filter + sort */}
      {aggregated.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            className="glass-input min-w-0 flex-1 text-[10px]"
            placeholder="filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          {(['total', 'recent'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSortMode(s)}
              className={clsx(
                'glass-button text-[10px]',
                sortMode === s && 'border-[color:var(--accent-strong)] text-white',
              )}
            >
              {s === 'total' ? '# likes' : 'recent'}
            </button>
          ))}
        </div>
      )}

      {/* liker list */}
      <div className="flex flex-col gap-1">
        {aggregated.map((a) => (
          <button
            type="button"
            key={a.did}
            onClick={() => navigate(`/profile/${encodeURIComponent(a.handle)}`)}
            className="glass flex items-center gap-2 px-2 py-1.5 text-left hover:bg-white/10"
          >
            {a.avatar ? (
              <img src={a.avatar} alt="" className="h-7 w-7 shrink-0 rounded-md object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="h-7 w-7 shrink-0 rounded-md bg-gradient-to-br from-sky-500/40 to-violet-500/40" />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[11px] font-semibold text-white">
                {a.displayName ?? '@' + a.handle}
              </div>
              <div className="flex flex-wrap gap-1 text-[9px] text-ink-400">
                <span>total <strong className="text-white">{a.total}</strong></span>
                {(['posts', 'replies', 'reposts'] as LikerSourceKind[])
                  .filter((s) => enabled.has(s) && a.perSource[s])
                  .map((s) => (
                    <span key={s}>{s} <strong className="text-white">{a.perSource[s]}</strong></span>
                  ))}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
